import { forwardRef } from "react";
import { TableCustomizationOptions, ASPECT_RATIOS, DEFAULT_OPTIONS, getOverlayDimensions } from "./ShareableTableCustomizer";
import { getVisibleRows } from "@/lib/tableUtils";

interface ShareableTableImageProps {
  managerName: string;
  clubName: string;
  tenurePeriod: string;
  season?: string;
  dateRange?: string;
  tableData: Array<{
    position: number;
    team: string;
    played: number;
    won: number;
    drawn: number;
    lost: number;
    gf: number;
    ga: number;
    gd: number;
    points: number;
    isManagerTeam: boolean;
  }>;
  options?: TableCustomizationOptions;
}

const THEMES = {
  default: {
    background: "linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #1e40af 100%)",
    headerBg: "#1e1b4b",
    headerText: "#ffffff",
    tableBg: "#ffffff",
    tableText: "#1f2937",
    accentText: "#fbbf24",
    subtitleText: "rgba(255,255,255,0.9)",
    seasonText: "rgba(255,255,255,0.7)",
  },
  dark: {
    background: "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)",
    headerBg: "#0f172a",
    headerText: "#f8fafc",
    tableBg: "#1e293b",
    tableText: "#e2e8f0",
    accentText: "#38bdf8",
    subtitleText: "rgba(248,250,252,0.9)",
    seasonText: "rgba(248,250,252,0.7)",
  },
  minimal: {
    background: "#ffffff",
    headerBg: "#f8fafc",
    headerText: "#0f172a",
    tableBg: "#ffffff",
    tableText: "#1f2937",
    accentText: "#3b82f6",
    subtitleText: "#475569",
    seasonText: "#64748b",
  },
  glass: {
    background: "rgba(15, 23, 42, 0.55)",
    headerBg: "rgba(15, 23, 42, 0.7)",
    headerText: "#f1f5f9",
    tableBg: "rgba(30, 41, 59, 0.5)",
    tableText: "#e2e8f0",
    accentText: "#67e8f9",
    subtitleText: "rgba(241, 245, 249, 0.85)",
    seasonText: "rgba(241, 245, 249, 0.65)",
  },
  transparent: {
    background: "transparent",
    headerBg: "transparent",
    headerText: "#ffffff",
    tableBg: "transparent",
    tableText: "#ffffff",
    accentText: "#fbbf24",
    subtitleText: "rgba(255,255,255,0.9)",
    seasonText: "rgba(255,255,255,0.7)",
  },
};

const TRANSPARENT_SHADOW = '0 1px 3px rgba(0,0,0,0.8)';

const getPositionStyles = (
  position: number,
  totalTeams: number,
  isManagerTeam: boolean,
  showZoneColors: boolean,
  theme: keyof typeof THEMES
): React.CSSProperties => {
  const themeColors = THEMES[theme];
  const isTransparent = theme === 'transparent';

  const baseStyle: React.CSSProperties = {
    backgroundColor: isTransparent ? 'rgba(0,0,0,0.4)' : themeColors.tableBg,
    color: isTransparent ? '#ffffff' : themeColors.tableText,
    textShadow: isTransparent ? TRANSPARENT_SHADOW : undefined,
  };

  if (!showZoneColors) {
    if (isManagerTeam) {
      return {
        backgroundColor: isTransparent ? 'rgba(59,130,246,0.5)' : '#3b82f6',
        color: '#ffffff', fontWeight: 600,
        textShadow: isTransparent ? TRANSPARENT_SHADOW : undefined,
      };
    }
    return baseStyle;
  }

  // Top 4 positions (Champions League)
  if (position <= 4) {
    if (isManagerTeam) {
      return {
        backgroundColor: isTransparent
          ? (position === 1 ? 'rgba(22,163,74,0.55)' : 'rgba(34,197,94,0.45)')
          : (position === 1 ? '#16a34a' : '#22c55e'),
        color: '#ffffff', fontWeight: position === 1 ? 'bold' : 600,
        textShadow: isTransparent ? TRANSPARENT_SHADOW : undefined,
      };
    }
    return {
      ...baseStyle,
      backgroundColor: isTransparent
        ? 'rgba(34,197,94,0.2)'
        : (theme === 'dark' || theme === 'glass' ? '#14532d' : '#dcfce7'),
    };
  }

  // Bottom 3 positions (Relegation)
  if (position > totalTeams - 3) {
    if (isManagerTeam) {
      return {
        backgroundColor: isTransparent ? 'rgba(220,38,38,0.5)' : '#dc2626',
        color: '#ffffff', fontWeight: 'bold',
        textShadow: isTransparent ? TRANSPARENT_SHADOW : undefined,
      };
    }
    return {
      ...baseStyle,
      backgroundColor: isTransparent
        ? 'rgba(220,38,38,0.2)'
        : (theme === 'dark' || theme === 'glass' ? '#450a0a' : '#fee2e2'),
    };
  }

  // Middle positions
  if (isManagerTeam) {
    return {
      backgroundColor: isTransparent ? 'rgba(59,130,246,0.5)' : '#3b82f6',
      color: '#ffffff', fontWeight: 600,
      textShadow: isTransparent ? TRANSPARENT_SHADOW : undefined,
    };
  }

  return baseStyle;
};

export const ShareableTableImage = forwardRef<HTMLDivElement, ShareableTableImageProps>(
  ({ managerName, clubName, tenurePeriod, season, dateRange, tableData, options = DEFAULT_OPTIONS }, ref) => {
    const totalTeams = tableData.length;
    const managerRow = tableData.find(row => row.isManagerTeam);
    const managerPosition = managerRow ? managerRow.position : null;
    const { rows: visibleRows, showTopEllipsis, showBottomEllipsis } = getVisibleRows(
      tableData,
      totalTeams,
      options.teamsToShow
    );

    const theme = THEMES[options.theme];
    const canvasDimensions = ASPECT_RATIOS[options.aspectRatio];
    const dimensions = getOverlayDimensions(canvasDimensions.width, canvasDimensions.height, options.overlayAspectRatio || 'match', options.overlayWidth);

    const BASE_WIDTH = 1080;
    const scale = dimensions.width / BASE_WIDTH;
    const titleSize = Math.max(Math.round(56 * scale), 24);
    const subtitleSize = Math.max(Math.round(28 * scale), 14);
    const seasonSize = Math.max(Math.round(22 * scale), 12);
    const positionSize = Math.max(Math.round(24 * scale), 13);
    const cellPadding = Math.max(Math.round(20 * scale), 8);
    const cellFontSize = Math.max(Math.round(28 * scale), 14);
    const headerFontSize = Math.max(Math.round(26 * scale), 13);

    // Build visible columns
    const cols = options.columns;
    const columnHeaders: { key: string; label: string; width?: number }[] = [
      { key: 'position', label: '#', width: Math.max(Math.round(90 * scale), 40) },
      { key: 'team', label: 'Team' },
    ];
    if (cols.played) columnHeaders.push({ key: 'played', label: 'P' });
    if (cols.won) columnHeaders.push({ key: 'won', label: 'W' });
    if (cols.drawn) columnHeaders.push({ key: 'drawn', label: 'D' });
    if (cols.lost) columnHeaders.push({ key: 'lost', label: 'L' });
    if (cols.gf) columnHeaders.push({ key: 'gf', label: 'GF' });
    if (cols.ga) columnHeaders.push({ key: 'ga', label: 'GA' });
    if (cols.gd) columnHeaders.push({ key: 'gd', label: 'GD' });
    if (cols.pts) columnHeaders.push({ key: 'pts', label: 'Pts' });

    const getCellValue = (row: typeof tableData[0], key: string) => {
      switch (key) {
        case 'position': return row.position;
        case 'team': return row.team;
        case 'played': return row.played;
        case 'won': return row.won;
        case 'drawn': return row.drawn;
        case 'lost': return row.lost;
        case 'gf': return row.gf;
        case 'ga': return row.ga;
        case 'gd': return row.gd > 0 ? `+${row.gd}` : row.gd;
        case 'pts': return row.points;
        default: return '';
      }
    };

    return (
      <div
        ref={ref}
        style={{
          width: dimensions.width,
          height: dimensions.height,
          padding: Math.max(Math.round(40 * scale), 16),
          background: theme.background,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header Section */}
        <div style={{ marginBottom: Math.max(Math.round(20 * scale), 10), textAlign: 'center' }}>
          <h1 style={{
            fontSize: titleSize,
            fontWeight: 'bold',
            color: theme.headerText,
            marginBottom: 8,
            lineHeight: 1.2,
            textShadow: options.theme === 'transparent' ? '0 2px 8px rgba(0,0,0,0.8)' : undefined,
          }}>
            {managerName}
          </h1>
          <p style={{ fontSize: subtitleSize, color: theme.subtitleText, margin: 0, textShadow: options.theme === 'transparent' ? '0 1px 4px rgba(0,0,0,0.7)' : undefined }}>
            {options.customSubtitle || `${clubName} • ${tenurePeriod}`}
          </p>
          {options.showSeason && season && (
            <p style={{ fontSize: seasonSize, color: theme.seasonText, marginTop: 6 }}>
              Season: {season}
            </p>
          )}
          {options.showPositionSummary && managerPosition && (
            <p style={{ fontSize: positionSize, color: theme.accentText, marginTop: 8, fontWeight: 600 }}>
              Position {managerPosition} of {totalTeams}
            </p>
          )}
        </div>

        {/* Table Section */}
        <div style={{
          backgroundColor: options.theme === 'transparent' ? 'transparent' : theme.tableBg,
          borderRadius: options.theme === 'transparent' ? 0 : 16,
          boxShadow: options.theme === 'transparent' ? 'none' : '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          overflow: 'hidden',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: options.theme === 'transparent' ? 'rgba(0,0,0,0.5)' : theme.headerBg }}>
                {columnHeaders.map(col => (
                  <th
                    key={col.key}
                    style={{
                      width: col.width,
                      textAlign: col.key === 'team' ? 'left' : 'center',
                      fontWeight: 'bold',
                      fontSize: headerFontSize,
                      padding: cellPadding,
                      color: theme.headerText,
                      textShadow: options.theme === 'transparent' ? TRANSPARENT_SHADOW : undefined,
                    }}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {showTopEllipsis && (
                <tr style={{ backgroundColor: options.theme === 'transparent' ? 'rgba(0,0,0,0.3)' : options.theme === 'dark' || options.theme === 'glass' ? 'rgba(55,65,81,0.6)' : '#f3f4f6' }}>
                  <td colSpan={columnHeaders.length} style={{ textAlign: 'center', fontSize: cellFontSize - 4, padding: 12, color: options.theme === 'transparent' ? 'rgba(255,255,255,0.7)' : '#6b7280', textShadow: options.theme === 'transparent' ? TRANSPARENT_SHADOW : undefined }}>
                    ···
                  </td>
                </tr>
              )}
              {visibleRows.map((row) => {
                const positionStyles = getPositionStyles(
                  row.position,
                  totalTeams,
                  row.isManagerTeam,
                  options.showZoneColors,
                  options.theme
                );
                return (
                  <tr key={row.position} style={positionStyles}>
                    {columnHeaders.map(col => (
                      <td
                        key={col.key}
                        style={{
                          textAlign: col.key === 'team' ? 'left' : 'center',
                          fontWeight: col.key === 'position' || col.key === 'pts' || (col.key === 'team' && row.isManagerTeam) ? 'bold' : 500,
                          fontSize: cellFontSize,
                          padding: cellPadding,
                        }}
                      >
                        {getCellValue(row, col.key)}
                      </td>
                    ))}
                  </tr>
                );
              })}
              {showBottomEllipsis && (
                <tr style={{ backgroundColor: options.theme === 'transparent' ? 'rgba(0,0,0,0.3)' : options.theme === 'dark' || options.theme === 'glass' ? 'rgba(55,65,81,0.6)' : '#f3f4f6' }}>
                  <td colSpan={columnHeaders.length} style={{ textAlign: 'center', fontSize: cellFontSize - 4, padding: 12, color: options.theme === 'transparent' ? 'rgba(255,255,255,0.7)' : '#6b7280', textShadow: options.theme === 'transparent' ? TRANSPARENT_SHADOW : undefined }}>
                    ···
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
);

ShareableTableImage.displayName = "ShareableTableImage";
